import { useState, useEffect } from 'react'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { checkoutUrl, enviro } from '../utils/common'
import { useForm } from 'react-hook-form'
import PricingTable from './PricingTable'
import { useEffectOnce } from 'react-use'
import { isValidPhoneNumber } from 'libphonenumber-js'
import clsx from 'clsx'

const soldOutSaturday = false
const soldOutSunday = false
const hideSaturdayCompletely = new Date() >= new Date('2026-10-24T18:00:00+02:00')

const TicketForm = () => {
	const {
		register,
		handleSubmit,
		setValue,
		setError,
		clearErrors,
		getValues,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: {
			saturday: 0,
			sunday: 0,
			weekend: 0,
			bags: 0,
		},
	})

	// Watch ticket values for reactive UI updates
	const watchedTickets = watch(['saturday', 'sunday', 'weekend', 'bags'])
	const [saturday, sunday, weekend, bags] = watchedTickets
	const totalTickets = (saturday || 0) + (sunday || 0) + (weekend || 0)
	const hasTickets = totalTickets > 0

	const [submitting, setSubmitting] = useState(false)
	const [bagsRemaining, setBagsRemaining] = useState(null)
	const [bagsLoading, setBagsLoading] = useState(true)

	useEffect(() => {
		const fetchBagInventory = async () => {
			try {
				const response = await fetch(`${checkoutUrl.replace('/checkout', '/bags')}`)
				if (response.ok) {
					const data = await response.json()
					setBagsRemaining(data.remaining)
				}
			} catch (error) {
				console.error('Error fetching bag inventory:', error)
			} finally {
				setBagsLoading(false)
			}
		}
		fetchBagInventory()
	}, [])

	const onSubmit = async (data) => {
		if (data?.saturday + data?.sunday + data?.weekend < 1) {
			setError('saturday', { type: 'custom', message: 'custom message' })
			return
		} else {
			clearErrors('saturday')
		}
		setSubmitting(true)

		// Get Facebook pixel cookies
		const fbData = window.getFacebookData ? window.getFacebookData() : {}

		const fbc = fbData?.fbc
		const fbp = fbData?.fbp

		const gclid = window?.gclid

		// Add Facebook pixel data to payload
		const payload = {
			...data,
			...(fbc && { fbc }),
			...(fbp && { fbp }),
			...(gclid && { gclid }),
		}

		const response = await fetch(checkoutUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})
		const jsonResponse = await response.json()

		if (jsonResponse.url) {
			window.location.href = jsonResponse.url
		} else {
			setSubmitting(false)
		}
	}

	useEffectOnce(() => {
		const refill = () => {
			const name = document.getElementById('name')
			const email = document.getElementById('email')
			const phone = document.getElementById('tel')
			const consent = document.getElementById('consent')
			const saturday = document.getElementById('saturday')
			const sunday = document.getElementById('sunday')
			const weekend = document.getElementById('weekend')

			setValue('name', name?.value || '')
			setValue('email', email?.value || '')
			setValue('tel', phone?.value || '')
			setValue('consent', consent?.checked || false)
			setValue('saturday', saturday?.value || 0)
			setValue('sunday', sunday?.value || 0)
			setValue('weekend', weekend?.value || 0)
		}
		setTimeout(refill, 1000)
	})

	const hasErrors = errors.name || errors.email || errors.consent || errors?.saturday || errors?.tel

	return (
		<form method='POST' onSubmit={handleSubmit(onSubmit)}>
			<input type='hidden' name='enviro' value={enviro} {...register('enviro')} />

			{/* Jubileumsväska Add-on */}
			{!bagsLoading && bagsRemaining > 0 && (
				<div className='mx-auto mb-12'>
					<div className='rounded-3xl border-2 border-amber-200 p-6'>
						<div className='flex flex-col items-center gap-4 md:flex-row md:items-start'>
							<div className='flex flex-col items-center flex-shrink-0'>
								<img
									src={
										'https://harmoniexpo.com/wp-content/uploads/2026/01/harmoni_expo_jubileumsvaska.png'
									}
									alt='Harmoni Expo Jubileumsväska'
									className='w-[200px] h-[200px] rounded-xl object-cover'
								/>
							</div>
							<div className='flex-1 text-center md:text-left'>
								<h3 className='font-display text-xl font-semibold text-amber-900 !m-0'>
									Jubileumsväska – 30 år!
									{bagsRemaining < 100 && (
										<span className='text-amber-700 font-normal text-base ml-2'>
											– endast {bagsRemaining} kvar
										</span>
									)}
								</h3>
								<p className='mt-1 text-amber-800 text-base'>
									Fira 30 år av Harmoni Expo med vår smidiga ryggsäcksväska med jubileumsdesign
									(Limited Edition)﹡. Lämnas ut på plats vid incheckning. Begränsat antal!
								</p>
								<div className='mt-3 flex flex-col items-center gap-2'>
									{!hasTickets && (
										<p className='text-amber-700 text-sm text-center md:text-left'>
											Välj minst en biljett för att lägga till väska
										</p>
									)}
									<div className='flex items-center gap-3'>
										<div className='flex items-baseline gap-1'>
											<span className='font-display text-2xl font-bold text-amber-900'>30</span>
											<span className='text-amber-900 font-medium'>kr</span>
										</div>
										<div className='inline-flex items-center rounded-2xl bg-amber-100 p-1'>
											<button
												type='button'
												aria-label='Minska antal väskor'
												disabled={!hasTickets}
												className={clsx(
													'flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200',
													!hasTickets
														? 'bg-amber-50 text-amber-300 cursor-not-allowed'
														: 'bg-white text-amber-600 shadow-sm hover:bg-amber-50 hover:text-amber-800 active:scale-95',
												)}
												onClick={() => {
													if (bags > 0) {
														setValue('bags', bags - 1)
													}
												}}
											>
												<MinusIcon className='h-4 w-4' aria-hidden='true' />
											</button>
											<input
												type='number'
												disabled={!hasTickets}
												className={clsx(
													'block w-12 bg-transparent border-0 text-center text-lg font-semibold text-amber-900',
													'focus:ring-0 focus:outline-none !mb-0 !border-none',
													!hasTickets && 'text-amber-400',
												)}
												{...register('bags', {
													min: 0,
													max: bagsRemaining,
													valueAsNumber: true,
												})}
												onChange={(e) => {
													let v = parseInt(e.target.value)
													if (v > bagsRemaining) {
														setValue('bags', bagsRemaining)
													} else if (v < 0 || isNaN(v)) {
														setValue('bags', 0)
													}
												}}
											/>
											<button
												type='button'
												aria-label='Öka antal väskor'
												disabled={!hasTickets || bags >= bagsRemaining}
												className={clsx(
													'flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200',
													!hasTickets || bags >= bagsRemaining
														? 'bg-amber-50 text-amber-300 cursor-not-allowed'
														: 'bg-amber-500 text-white shadow-sm hover:bg-amber-600 active:scale-95',
												)}
												onClick={() => {
													if (bags < bagsRemaining) {
														setValue('bags', bags + 1)
													}
												}}
											>
												<PlusIcon className='h-4 w-4' aria-hidden='true' />
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Pricing Cards */}
			<div
				className={clsx(
					'grid gap-6 mb-12',
					!hideSaturdayCompletely ? 'md:grid-cols-3' : 'max-w-sm mx-auto',
				)}
			>
				{!hideSaturdayCompletely && (
					<PricingTable
						name='Lördag'
						price={180}
						description='Tillträde den 24 oktober.'
						inputName='saturday'
						register={register}
						setValue={setValue}
						getValues={getValues}
						clearErrors={clearErrors}
						soldOut={soldOutSaturday}
					/>
				)}
				<PricingTable
					name='Söndag'
					price={180}
					description='Tillträde den 25 oktober.'
					inputName='sunday'
					register={register}
					setValue={setValue}
					getValues={getValues}
					clearErrors={clearErrors}
					soldOut={soldOutSunday}
				/>
				{!hideSaturdayCompletely && (
					<PricingTable
						name='Helgpass'
						price={280}
						description='Tillträde båda dagarna.'
						inputName='weekend'
						mostPopular={true}
						register={register}
						setValue={setValue}
						getValues={getValues}
						clearErrors={clearErrors}
					/>
				)}
			</div>

			{/* Form Section */}
			<div className='max-w-lg mx-auto'>
				<div className='space-y-5'>
					{/* Name */}
					<div>
						<label htmlFor='name' className='block text-sm font-medium text-sage-700 mb-2'>
							Namn <span className='text-harmoni-500'>*</span>
						</label>
						<input
							id='name'
							name='name'
							type='text'
							autoComplete='name'
							{...register('name', { required: true })}
							className={clsx(
								'input-field',
								errors.name && 'border-harmoni-300 focus:border-harmoni-400 focus:ring-harmoni-200',
							)}
						/>
					</div>

					{/* Email */}
					<div>
						<label htmlFor='email' className='block text-sm font-medium text-sage-700 mb-2'>
							Email <span className='text-harmoni-500'>*</span>
						</label>
						<input
							id='email'
							name='email'
							type='email'
							autoComplete='email'
							{...register('email', {
								required: true,
								pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/i,
							})}
							className={clsx(
								'input-field',
								errors.email && 'border-harmoni-300 focus:border-harmoni-400 focus:ring-harmoni-200',
							)}
						/>
					</div>

					{/* Phone */}
					<div>
						<label htmlFor='tel' className='block text-sm font-medium text-sage-700 mb-2'>
							Telefonnummer
							<span className='text-sage-600 font-normal ml-2'>(för SMS-biljett)</span>
						</label>
						<input
							id='tel'
							name='tel'
							type='tel'
							autoComplete='tel'
							placeholder='+46 70 123 45 67'
							{...register('tel', {
								validate: (value, _) => value === '' || isValidPhoneNumber(value, 'SE'),
							})}
							className={clsx(
								'input-field',
								errors.tel && 'border-harmoni-300 focus:border-harmoni-400 focus:ring-harmoni-200',
							)}
						/>
					</div>

					{/* Consent */}
					<div className='flex items-start gap-3 pt-2'>
						<input
							id='consent'
							name='consent'
							type='checkbox'
							className='mt-1 h-5 w-5 rounded border-sage-300 text-sage-600 focus:ring-sage-500 cursor-pointer'
							{...register('consent', { required: true })}
						/>
						<label htmlFor='consent' className='text-sage-700 cursor-pointer'>
							Jag godkänner de{' '}
							<a
								href='/villkor-biljettkop/'
								target='_blank'
								rel='noreferrer'
								className='text-harmoni-600 hover:text-harmoni-700 underline underline-offset-2'
							>
								allmänna villkoren
							</a>
						</label>
					</div>
				</div>

				{/* Error Messages */}
				{hasErrors && (
					<div className='mt-6 rounded-2xl bg-harmoni-50 border border-harmoni-200 p-4'>
						<div className='flex gap-3'>
							<ExclamationCircleIcon className='h-6 w-6 text-harmoni-500 flex-shrink-0' />
							<div className='text-sm text-harmoni-700'>
								<ul className='space-y-1'>
									{errors?.saturday && <li>Du måste välja minst en biljett</li>}
									{errors.name && <li>Vänligen skriv in ditt namn</li>}
									{errors.email && <li>Vänligen skriv in en giltig e-postadress</li>}
									{errors.tel && <li>Vänligen skriv in ett giltigt svenskt nummer</li>}
									{errors.consent && <li>Vänligen acceptera villkoren för att fortsätta</li>}
								</ul>
							</div>
						</div>
					</div>
				)}

				{/* Submit Button */}
				<div className='mt-8'>
					<button disabled={submitting} type='submit' className='btn-primary w-full text-lg'>
						{submitting ? (
							<span className='flex items-center justify-center gap-2'>
								<svg
									className='animate-spin h-5 w-5'
									xmlns='http://www.w3.org/2000/svg'
									fill='none'
									viewBox='0 0 24 24'
								>
									<circle
										className='opacity-25'
										cx='12'
										cy='12'
										r='10'
										stroke='currentColor'
										strokeWidth='4'
									/>
									<path
										className='opacity-75'
										fill='currentColor'
										d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
									/>
								</svg>
								Laddar...
							</span>
						) : (
							'Till kassan'
						)}
					</button>
				</div>
			</div>
		</form>
	)
}

export default TicketForm
